import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listKafDomains, listKafProgress, getKafStreak, type KafDomain } from "@/lib/kaf-academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { Flame, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/academy/kaf/progress")({
  head: () => ({ meta: [{ title: "Your Journey — KAfrequency" }] }),
  component: ProgressPage,
});

function ProgressPage() {
  const domainsFn = useServerFn(listKafDomains);
  const progressFn = useServerFn(listKafProgress);
  const streakFn = useServerFn(getKafStreak);
  const domains = useQuery({ queryKey: ["kaf", "domains"], queryFn: () => domainsFn() });
  const progress = useQuery({ queryKey: ["kaf", "progress"], queryFn: () => progressFn() });
  const streak = useQuery({ queryKey: ["kaf", "streak"], queryFn: () => streakFn() });

  const completed = (progress.data ?? []).filter((p) => p.status === "completed").length;
  const totalMinutes = Math.round(((progress.data ?? []).reduce((n, p) => n + (p.seconds_spent ?? 0), 0)) / 60);

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      <div className="absolute inset-0 opacity-70"><LivingBackdrop hue={44} density={90} /></div>
      <section className="relative z-10 mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Your Journey</p>
        <h1 className="mt-2 font-serif text-4xl text-gold-luminous">The Living Record</h1>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <Stat label="Current streak" value={`${streak.data?.current_streak ?? 0} days`} icon={<Flame size={16} />} />
          <Stat label="Lessons completed" value={String(completed)} icon={<CheckCircle2 size={16} />} />
          <Stat label="Study time" value={`${totalMinutes} min`} />
        </div>

        <h2 className="mt-10 font-serif text-2xl text-gold-luminous">Domains</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {((domains.data ?? []) as KafDomain[]).map((d) => (
            <Link key={d.id} to="/academy/kafrequency/$domain" params={{ domain: d.slug }}
              className="group rounded-2xl border hairline p-4 transition hover:border-gold/60">
              <div className="text-xs uppercase tracking-widest" style={{ color: `hsl(${d.hue} 85% 65%)` }}>
                {d.tagline ?? "Domain"}
              </div>
              <div className="mt-1 font-serif text-lg text-gold-luminous">{d.name}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-2xl border hairline bg-obsidian/70 p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold">
        {icon} {label}
      </div>
      <div className="mt-2 font-serif text-3xl text-gold-luminous">{value}</div>
    </div>
  );
}
